import React from 'react';
import { Link } from 'react-router-dom';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crisp/crisp-ui/cabbage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faDesktop,
  faMagnifyingGlass,
  faMoon,
  faRightFromBracket,
  faShield,
  faSparkles,
  faSun,
} from '@crisp/crisp-ui/icons/classic/regular';
import { useTheme, setThemePreference, type ThemePreference } from '@crisp/ui-toolkit';
import { useCrispAuth } from '../../lib/auth0';
import { modShortcut } from '../../lib/platform';
import { DEFAULT_SITE, type SiteConfig } from '../../lib/sites';
import { cn } from '../../lib/cn';
import styles from './Header.module.css';

interface HeaderProps {
  routePrefix?: string;
  site?: SiteConfig;
  previewBranch?: string;
  onOpenSearch: () => void;
  onOpenAssistant: (initialQuery?: string) => void;
  onToggleSidebar?: () => void;
  isSidebarOpen?: boolean;
}

const THEME_OPTIONS: Array<{ id: ThemePreference; label: string; icon: typeof faSun }> = [
  { id: 'system', label: 'System', icon: faDesktop },
  { id: 'light', label: 'Light', icon: faSun },
  { id: 'dark', label: 'Dark', icon: faMoon },
];

function themeIcon(preference: ThemePreference) {
  if (preference === 'system') return faDesktop;
  if (preference === 'dark') return faMoon;
  return faSun;
}

export const Header: React.FC<HeaderProps> = ({
  routePrefix = '',
  site = DEFAULT_SITE,
  previewBranch,
  onOpenSearch,
  onOpenAssistant,
}) => {
  const { preference } = useTheme({ default: 'dark' });
  const {
    isAuthenticated,
    user,
    loginWithRedirect,
    logout,
    isMock,
    setMockRoles,
    isAdmin,
  } = useCrispAuth();
  const themeMenu = (triggerLabel: string) => (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={triggerLabel}
            iconPrimary={<FontAwesomeIcon icon={themeIcon(preference)} />}
          />
        }
      />
      <DropdownMenuContent align="end">
        <DropdownMenuGroup>
          {THEME_OPTIONS.map((option) => (
            <DropdownMenuItem
              key={option.id}
              onClick={() => setThemePreference(option.id)}
            >
              <FontAwesomeIcon icon={option.icon} />
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header id="navbar" className={styles.root}>
      <div className={styles.row}>
        <div className={styles.brand}>
          <Link to={routePrefix || '/'} className={styles.logoLink}>
            <span className="sr-only">{site.name} Documentation</span>
            <img src={site.logo.light} alt="" className={cn(styles.logo, styles.logoLight)} />
            <img src={site.logo.dark} alt="" className={cn(styles.logo, styles.logoDark)} />
          </Link>
          {previewBranch && (
            <span className={styles.previewBadge}>Preview: {previewBranch}</span>
          )}
        </div>

        <div className={styles.searchCluster}>
          <button
            type="button"
            onClick={onOpenSearch}
            id="search-bar-entry"
            aria-label="Open search"
            className={styles.searchTrigger}
          >
            <span className={styles.searchLabel}>
              <FontAwesomeIcon icon={faMagnifyingGlass} className="icon-slot" />
              <span>Search...</span>
            </span>
            <span className={styles.shortcut}>{modShortcut('K')}</span>
          </button>
          <button
            type="button"
            onClick={() => onOpenAssistant()}
            id="assistant-entry"
            aria-label="Ask AI"
            className={styles.askTrigger}
          >
            <FontAwesomeIcon icon={faSparkles} className="icon-slot" />
            <span>Ask AI</span>
            <span className={styles.shortcut}>{modShortcut('I')}</span>
          </button>
        </div>

        <div className={styles.desktopActions}>
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                nativeButton={false}
                render={
                  <button type="button" className={styles.profileTrigger}>
                    <Avatar size="sm">
                      {user?.picture ? <AvatarImage src={user.picture} alt="" /> : null}
                      <AvatarFallback>{user?.name?.[0] || 'U'}</AvatarFallback>
                    </Avatar>
                    <span className={styles.profileName}>{user?.name || user?.email}</span>
                    <FontAwesomeIcon icon={faChevronDown} className="icon-slot" />
                  </button>
                }
              />
              <DropdownMenuContent align="end">
                <DropdownMenuGroup>
                  <DropdownMenuLabel>
                    <div className={styles.profileMeta}>
                      <p className={styles.profileMetaName}>{user?.name || 'Crisp User'}</p>
                      <p className={styles.profileMetaEmail}>{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                </DropdownMenuGroup>
                {isMock && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                      <DropdownMenuLabel>
                        <span className={styles.mockLabel}>Mock Auth</span>
                      </DropdownMenuLabel>
                      {[
                        {
                          id: 'all',
                          label: 'Full Access (All Roles)',
                          roles: [
                            'authenticated',
                            'walmart',
                            'mission-control',
                            'target',
                            'ai-master-data',
                            'solys',
                          ],
                        },
                        {
                          id: 'admin',
                          label: 'Admin (Authoring)',
                          roles: [
                            'authenticated',
                            'admin',
                            'walmart',
                            'mission-control',
                            'target',
                            'ai-master-data',
                            'solys',
                          ],
                        },
                        {
                          id: 'walmart',
                          label: 'Walmart Only',
                          roles: ['authenticated', 'walmart'],
                        },
                        {
                          id: 'public',
                          label: 'Public Only',
                          roles: [],
                        },
                      ].map((preset) => (
                        <DropdownMenuItem
                          key={preset.id}
                          onClick={() => setMockRoles?.(preset.roles)}
                        >
                          {preset.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuGroup>
                  </>
                )}
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                      <DropdownMenuItem render={<Link to="/admin/author" />}>
                        <FontAwesomeIcon icon={faShield} />
                        Authoring
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem variant="destructive" onClick={() => logout()}>
                    <FontAwesomeIcon icon={faRightFromBracket} />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => loginWithRedirect()}>
              Log In
            </Button>
          )}
          {themeMenu('Change theme preference')}
        </div>

        <div className={styles.mobileActions}>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Open search"
            onClick={onOpenSearch}
            iconPrimary={<FontAwesomeIcon icon={faMagnifyingGlass} />}
          />
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Ask AI"
            onClick={() => onOpenAssistant()}
            iconPrimary={<FontAwesomeIcon icon={faSparkles} />}
          />
          {themeMenu('More options')}
        </div>
      </div>
    </header>
  );
};

export default Header;
